import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GuideProfileView } from './profiles/GuideProfileView';
import { RescheduleModal } from './booking/RescheduleModal';
import { PackageOfferCard } from './packages/PackageOfferCard';
import { ActiveEngagementCard } from './packages/ActiveEngagementCard';
import { seekerDataService } from '../services/seekerDataService';
import { type GuideDetails } from '../services/matchingService';
import { getSupabaseClient } from '../lib/supabase';
import { type HomePageData, type Resource, type Session } from '../types';
import { formatSessionTimeInSeekerTimezone, getBrowserTimezone, getTimezoneAbbreviation, isToday, isTomorrow, formatTimeInTimezone, formatShortDateInTimezone } from '../utils/dateUtils';
import { AddToCalendarButton } from './AddToCalendarButton';

export const HomeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<HomePageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGuideProfile, setShowGuideProfile] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [sessionForModal, setSessionForModal] = useState<Session | null>(null);
  const [guideResources, setGuideResources] = useState<Resource[]>([]);
  const [adminResources, setAdminResources] = useState<Resource[]>([]);
  const [seekerTimezone, setSeekerTimezone] = useState<string | null>(null);
  const [confirmingSession, setConfirmingSession] = useState(false);
  const [chatDisabledPopup, setChatDisabledPopup] = useState(false);
  const supabase = useMemo(() => getSupabaseClient(), []);

  const loadData = async () => {
    try {
      console.log('[HomeDashboard] Starting to load data...');
      setLoading(true);
      setError(null);
      const homePageData = await seekerDataService.getHomePageData();
      console.log('[HomeDashboard] Received home page data:', {
        hasProfile: !!homePageData.profile,
        hasMatch: !!homePageData.match,
        hasUpcomingSession: !!homePageData.upcomingSession,
        upcomingSessionData: homePageData.upcomingSession,
        hasPackageOffer: !!homePageData.packageOffer,
        hasActiveEngagement: !!homePageData.activeEngagement,
        activeEngagementsCount: homePageData.activeEngagements?.length,
        hasCompletedConsultation: homePageData.hasCompletedConsultation
      });
      setData(homePageData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      console.error('[HomeDashboard] Failed to load home page data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadGuideResources = async (guideId: string) => {
    try {
      const { data: resourcesData, error: resourcesError } = await supabase
        .from('resources')
        .select('*')
        .eq('uploaded_by', guideId)
        .eq('seeker_viewable', true)
        .eq('is_active', true)
        .order('uploaded_at', { ascending: false });

      if (resourcesError) {
        console.error('Failed to load guide resources:', resourcesError);
        return;
      }

      setGuideResources(resourcesData || []);
    } catch (err) {
      console.error('Failed to load guide resources:', err);
    }
  };

  const loadAdminResources = async (guideResourceParentIds: string[]) => {
    try {
      const { data: resourcesData, error: resourcesError } = await supabase
        .from('resources')
        .select('*')
        .eq('admin_public', true)
        .eq('seeker_viewable', true)
        .eq('is_active', true)
        .order('uploaded_at', { ascending: false });

      if (resourcesError) {
        console.error('Failed to load admin resources:', resourcesError);
        return;
      }

      // Filter out admin resources that have been cloned by the guide
      const filtered = (resourcesData || []).filter(
        (resource) => !guideResourceParentIds.includes(resource.id)
      );

      setAdminResources(filtered);
    } catch (err) {
      console.error('Failed to load admin resources:', err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Refetch when the tab regains focus (handles guide-initiated reschedules)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Load seeker timezone
  useEffect(() => {
    const loadSeekerTimezone = async () => {
      const tz = await seekerDataService.getSeekerTimezone();
      setSeekerTimezone(tz || getBrowserTimezone());
    };
    if (!loading && data) {
      loadSeekerTimezone();
    }
  }, [loading, data]);

  const matchStatus = data?.match?.status ?? null;
  const activeEngagementsList = data?.activeEngagements ?? (data?.activeEngagement ? [data.activeEngagement] : []);

  useEffect(() => {
    if (!loading && matchStatus && matchStatus !== 'active') {
      navigate('/onboarding/finding-match', { replace: true });
    }
  }, [loading, matchStatus, navigate]);

  // Redirect to match page only if there's no match yet
  useEffect(() => {
    if (loading || !data) return;

    const hasActiveMatch = matchStatus === 'active' || !matchStatus;
    const guide = data?.match?.guide_details;

    // Only redirect if there's no guide match at all
    if (hasActiveMatch && !guide) {
      navigate('/onboarding/match', { replace: true });
      return;
    }

    // If matched with a guide but never had a session, send to scheduling
    if (hasActiveMatch && guide && !data.upcomingSession && !data.hasCompletedConsultation && !data.activeEngagement && !data.packageOffer) {
      navigate('/onboarding/match', { replace: true, state: { openScheduler: true } });
    }
  }, [loading, data, matchStatus, navigate]);

  // Load resources when guide data is available
  useEffect(() => {
    const guideId = data?.match?.guide_details?.id;
    if (guideId) {
      // Load guide resources first
      loadGuideResources(guideId).then(async () => {
        // Get parent IDs from guide resources to filter admin resources
        const { data: guideResourcesData } = await supabase
          .from('resources')
          .select('parent_id')
          .eq('uploaded_by', guideId)
          .eq('seeker_viewable', true)
          .eq('is_active', true)
          .not('parent_id', 'is', null);

        const parentIds = (guideResourcesData || [])
          .map(r => r.parent_id)
          .filter((id): id is string => id !== null);

        // Load admin resources excluding those that have been customized by guide
        loadAdminResources(parentIds);
      });
    } else {
      // If no guide, just load admin resources
      loadAdminResources([]);
    }
  }, [data?.match?.guide_details?.id]);

  const sessionExpectations = [
    'Get to know your Guide',
    'Talk about your specific needs',
    'Learn about your guide\'s experience'
  ];

  const formatSessionDateTime = (session: Session) => {
    const tz = seekerTimezone || getBrowserTimezone();

    // Use UTC start_time with timezone-aware formatting when available
    if (session.start_time && tz) {
      const timeStr = formatTimeInTimezone(session.start_time, tz);
      const tzAbbrev = getTimezoneAbbreviation(session.start_time, tz);
      const timePart = tzAbbrev ? `${timeStr} ${tzAbbrev}` : timeStr;

      if (isToday(session.start_time, tz)) {
        return `Today ${timePart}`;
      }
      if (isTomorrow(session.start_time, tz)) {
        return `Tomorrow ${timePart}`;
      }
      const dateStr = formatShortDateInTimezone(session.start_time, tz);
      return `${dateStr}, ${timePart}`;
    }

    // Fallback for legacy sessions
    const { formattedDate, formattedTime } = formatSessionTimeInSeekerTimezone(
      session.start_time,
      seekerTimezone,
      session.session_date,
      session.session_time
    );
    try {
      const dateObj = new Date(session.session_date + 'T00:00:00');
      const dateStr = dateObj.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
      return `${dateStr} at ${formattedTime}`;
    } catch {
      return `${formattedDate} at ${formattedTime}`;
    }
  };

  const getSessionTypeLabel = (type: string) => {
    switch (type) {
      case 'consultation':
        return 'Consultation Session';
      case 'preparation':
        return 'Preparation Session';
      case 'integration':
        return 'Integration Session';
      case 'journey':
        return 'Journey Session';
      case 'follow_up':
        return 'Follow-up Session';
      default:
        return 'Session';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
              <p className="text-[#2C2731]/70">Loading your dashboard...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
          <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 items-center justify-center px-4">
            <div className="max-w-sm text-center">
              <p className="mb-4 text-sm text-red-600">Error: {error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-full border border-[#304031] bg-[#304031] px-6 py-3 text-sm font-semibold text-[#FFFCEF] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.55)] transition-transform hover:-translate-y-0.5 hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2"
              >
                Retry
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const isMatchActive = !matchStatus || matchStatus === 'active';
  const guide = isMatchActive ? data?.match?.guide_details : null;
  const session = data?.upcomingSession;
  const profile = data?.profile;
  const packageOffer = data?.packageOffer ?? null;
  const activeEngagement = data?.activeEngagement ?? null;
  const hasCompletedConsultation = data?.hasCompletedConsultation ?? false;
  const hasScheduledConsultation = session?.session_type === 'consultation' && (session?.session_status === 'scheduled' || session?.session_status === 'confirmed');
  const hideConsultationSection = !session && hasCompletedConsultation;

  console.log('[HomeDashboard] Render state:', {
    isMatchActive,
    hasGuide: !!guide,
    hasSession: !!session,
    sessionDetails: session ? {
      id: session.id,
      type: session.session_type,
      status: session.session_status,
      date: session.session_date,
      time: session.session_time
    } : null,
    hasPackageOffer: !!packageOffer,
    hasActiveEngagement: !!activeEngagement,
    hasCompletedConsultation,
    hideConsultationSection
  });

  const handlePurchaseOffer = () => {
    navigate('/home/package-checkout');
  };

  const convertToGuideDetails = (guideData: any): GuideDetails | null => {
    if (!guideData) return null;

    return {
      guide_id: guideData.id,
      name: guideData.name,
      location: guideData.location,
      bio: guideData.description,
      experience_types: guideData.experience_types || [],
      support_types: guideData.support_types || [],
      focus_areas: guideData.specializations || [],
      special_groups: guideData.special_groups || [],
      religious_beliefs: guideData.religious_beliefs || [],
      backgroundChecked: guideData.backgroundChecked
    };
  };

  const handleViewProfile = () => {
    if (guide) {
      setShowGuideProfile(true);
    }
  };

  const handleCloseProfile = () => {
    setShowGuideProfile(false);
  };

  const handleReschedule = () => {
    if (session) {
      setSessionForModal(session);
      setShowRescheduleModal(true);
    }
  };

  const handleCloseReschedule = () => {
    setShowRescheduleModal(false);
    setSessionForModal(null);
  };

  const handleRescheduleComplete = () => {
    setShowRescheduleModal(false);
    setSessionForModal(null);
    loadData();
  };

  const handleJoinSession = () => {
    if (session?.location === 'link' && session.session_link) {
      window.open(session.session_link, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate('/home/waiting-room');
  };

  const handleConfirmSession = async () => {
    if (!session || confirmingSession) return;
    setConfirmingSession(true);
    try {
      await seekerDataService.confirmSession(session.id);
      loadData(); // Refresh data to show updated status
    } catch (err) {
      console.error('Failed to confirm session:', err);
      alert('Failed to confirm session. Please try again.');
    } finally {
      setConfirmingSession(false);
    }
  };

  const canChat = !!guide;

  const handleChatClick = () => {
    if (canChat) {
      navigate('/home/chat');
    } else {
      setChatDisabledPopup(true);
      setTimeout(() => setChatDisabledPopup(false), 3000);
    }
  };

  const dashboardView = (
    <div className="flex min-h-full flex-col space-y-6 pb-24 text-[#2C2731]">
      {/* Welcome section - flat text, no card (Ticket #3) */}
      <div className="pt-1">
        <h1 className="font-recoleta text-2xl text-[#2C2731] ph-mask">
          Welcome, {profile?.name ? profile.name.split(' ')[0] : 'Seeker'}
        </h1>
        <p className="text-sm text-[#2C2731]/70">Ready to continue your journey?</p>
      </div>

      <div className="space-y-6">
        {/* === SECTION 1: Action-required items at top (Ticket #1) === */}

        {/* Upcoming Session / Consultation - always first when present */}
        {(!hideConsultationSection && (!packageOffer || hasScheduledConsultation) && (!activeEngagement || hasScheduledConsultation)) && (
          <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-[#2C2731]">
              {session?.session_type === 'consultation' ? 'Your Consultation' : 'Upcoming Session'}
            </h2>

            {session ? (
              <div className="mb-4">
                <h3 className="mb-1 font-medium text-[#2C2731]">{getSessionTypeLabel(session.session_type)}</h3>
                <p className="mb-3 text-sm text-[#2C2731]/70">With {session.guide_name || 'Your Guide'}</p>

                <div className="mb-4 flex items-center space-x-4 text-sm text-[#2C2731]/80">
                  <div className="flex items-center space-x-1">
                    <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{formatSessionDateTime(session)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {session.location === 'link' ? (
                      <>
                        <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        <span>External Link</span>
                      </>
                    ) : session.location === 'in_person' ? (
                      <>
                        <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>In Person</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span>Video Call</span>
                      </>
                    )}
                  </div>
                </div>

                {session.session_status === 'scheduled' && session.scheduled_by === 'guide' ? (
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-blue-800">Confirm This Session</p>
                        <p className="text-xs text-blue-700">Your guide has scheduled this session. Please confirm to proceed.</p>
                      </div>
                    </div>
                  </div>
                ) : session.session_status === 'scheduled' ? (
                  <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-yellow-800">Awaiting Confirmation</p>
                        <p className="text-xs text-yellow-700">Your guide will confirm this session shortly</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-col space-y-2">
                  {session.session_status === 'scheduled' && session.scheduled_by === 'guide' ? (
                    <button
                      className="w-full rounded-lg bg-[#304031] py-2 text-sm font-medium text-[#FFFCEF] transition-colors hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleConfirmSession}
                      disabled={confirmingSession}
                    >
                      {confirmingSession ? 'Confirming...' : 'Confirm Session'}
                    </button>
                  ) : (
                    <button
                      className="w-full rounded-lg bg-[#304031] py-2 text-sm font-medium text-[#FFFCEF] transition-colors hover:bg-[#304031]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFCEF] focus-visible:ring-offset-2 focus-visible:ring-offset-[#304031] disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleJoinSession}
                      disabled={session.session_status === 'scheduled'}
                    >
                      {session.session_status === 'scheduled' ? 'Pending Confirmation' : session.location === 'link' ? 'Open External Link' : 'Enter'}
                    </button>
                  )}
                  <button
                    className="w-full rounded-lg border border-[#304031] bg-[#FFFCEF] py-2 text-sm font-medium text-[#304031] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCEF]"
                    onClick={handleReschedule}
                  >
                    Reschedule
                  </button>
                </div>

                <div className="mt-3 flex justify-center">
                  <AddToCalendarButton sessionId={session.id} />
                </div>

                <div className="mt-4">
                  <h4 className="mb-3 font-medium text-[#2C2731]">What to expect in your session</h4>
                  <div className="space-y-2">
                    {sessionExpectations.map((expectation, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="h-2 w-2 rounded-full bg-[#2C2731]" />
                        <span className="text-sm text-[#2C2731]/70">{expectation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="mb-2 text-sm text-[#2C2731]/70">No upcoming sessions</p>
                <p className="text-xs text-[#2C2731]/60">
                  Sessions will appear here once scheduled with your guide
                </p>
              </div>
            )}
          </div>
        )}

        {/* Pending Package Offer - action-required, above guide card (Ticket #1) */}
        {packageOffer && guide && packageOffer.guideId === guide.id && (
          <PackageOfferCard offer={packageOffer} guide={guide ?? undefined} onPurchase={handlePurchaseOffer} />
        )}

        {/* Awaiting Package Message - show when consultation completed but no package offer or active engagement */}
        {hasCompletedConsultation && !packageOffer && activeEngagementsList.length === 0 && !session && (
          <div className="rounded-2xl border border-[#2C2731]/30 bg-[#FFFCEF] p-5 text-center">
            <svg className="mx-auto mb-3 h-8 w-8 text-[#304031]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="font-recoleta text-lg text-[#2C2731]/70">
              Your guide is crafting a personalized package just for you.
            </p>
            <p className="mt-1 text-sm text-[#2C2731]/50">We'll notify you as soon as it's ready.</p>
          </div>
        )}

        {/* Active Engagement (Accepted Package) */}
        {activeEngagementsList.length > 0 && (
          <ActiveEngagementCard
            offers={activeEngagementsList}
            offer={data?.activeEngagement ?? undefined}
            guide={guide ?? undefined}
            onReschedule={(packageSession) => {
              setSessionForModal(packageSession as Session);
              setShowRescheduleModal(true);
            }}
          />
        )}

        {/* === SECTION 2: Guide card below action items (Tickets #1, #2, #4) === */}
        <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
          {guide ? (
            <>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#304031]">
                  <svg className="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-[#2C2731]">Your Guide</h2>
              </div>

              <div className="mb-4 flex items-start space-x-3">
                <div className="relative">
                  {guide.image ? (
                    <img src={guide.image} alt={guide.name} className="h-14 w-14 rounded-full object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#E8DFBD]">
                      <svg className="h-8 w-8 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-[#2C2731] ph-mask">{guide.name}</h3>
                  <p className="mb-2 text-sm text-[#2C2731]/70">
                    {guide.title} • {guide.experience}
                  </p>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {guide.specializations.map((spec: string, index: number) => (
                      <span key={index} className="rounded border border-[#2C2731]/40 bg-[#FFFCEF] px-2 py-1 text-xs text-[#2C2731]">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <p className="mb-4 text-sm text-[#2C2731]/80">{guide.description}</p>

              {/* Secondary CTA: View Profile (Ticket #4) */}
              <button
                className="w-full rounded-lg border border-[#304031] bg-[#FFFCEF] py-3 text-sm font-medium text-[#304031] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2"
                onClick={handleViewProfile}
              >
                View Full Profile
              </button>

              {/* Chat CTA inside guide card (Ticket #2) */}
              <div className="mt-3">
                <button
                  className={`w-full rounded-lg py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    canChat
                      ? 'border border-[#304031] bg-[#FFFCEF] text-[#304031] hover:bg-[#E8DFBD] focus-visible:ring-[#304031]'
                      : 'border border-[#2C2731]/20 bg-[#FFFCEF] text-[#2C2731]/40 cursor-default'
                  }`}
                  onClick={handleChatClick}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h6m1 8l4-4H6a2 2 0 01-2-2V8a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-3z" />
                    </svg>
                    <span className="ph-mask">Chat with {guide.name.split(' ')[0]}</span>
                  </div>
                </button>
                {chatDisabledPopup && (
                  <div className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-center text-xs text-yellow-800">
                    Chat is available once you're matched with a guide
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#304031]/10">
                <svg className="h-7 w-7 text-[#304031]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h2 className="mb-2 text-lg font-semibold text-[#2C2731]">We're finding your guide</h2>
              <p className="mb-2 text-sm text-[#2C2731]/70">Our team is searching for the perfect guide match for you. We'll notify you as soon as we find someone aligned with your needs.</p>
              <a href="mailto:support@guidescollective.com" className="mt-2 inline-flex text-xs font-medium text-[#304031] underline underline-offset-2">Contact support</a>
            </div>
          )}
        </div>

        {/* Your Guide's Resources */}
        {guideResources.length > 0 && (
          <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#2C2731]">Your Guide's Resources</h2>
              {guideResources.length > 3 && (
                <button className="text-sm font-medium text-[#2C2731] underline-offset-4 hover:underline">
                  See All
                </button>
              )}
            </div>

            <div className="space-y-3">
              {guideResources.slice(0, 3).map((resource) => (
                <div key={resource.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#E8DFBD]">
                      {resource.thumbnail_url ? (
                        <img src={resource.thumbnail_url} alt={resource.name} className="h-10 w-10 object-cover" />
                      ) : (
                        <svg className="h-6 w-6 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-[#2C2731]">{resource.name}</h4>
                      <p className="text-xs text-[#2C2731]/70 ph-mask">by {guide?.name || 'Your Guide'}</p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const { data: fileData, error: downloadError } = await supabase.storage.from('resources').download(resource.file_path);

                        if (downloadError || !fileData) {
                          throw downloadError || new Error('Unable to download resource');
                        }

                        const blobUrl = URL.createObjectURL(fileData);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = resource.file_name || 'resource';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                      } catch (err) {
                        console.error('Failed to download resource', err);
                        alert('Unable to download resource. Please contact your guide.');
                      }
                    }}
                    className="rounded border border-[#304031] bg-transparent px-3 py-1 text-sm font-medium text-[#304031] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Guides Collective's Resources */}
        {adminResources.length > 0 && (
          <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#2C2731]">Guides Collective's Resources</h2>
              {adminResources.length > 3 && (
                <button className="text-sm font-medium text-[#2C2731] underline-offset-4 hover:underline">
                  See All
                </button>
              )}
            </div>

            <div className="space-y-3">
              {adminResources.slice(0, 3).map((resource) => (
                <div key={resource.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[#E8DFBD]">
                      {resource.thumbnail_url ? (
                        <img src={resource.thumbnail_url} alt={resource.name} className="h-10 w-10 object-cover" />
                      ) : (
                        <svg className="h-6 w-6 text-[#2C2731]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-[#2C2731]">{resource.name}</h4>
                      <p className="text-xs text-[#2C2731]/70">by {resource.author || 'GuidesCollective Team'}</p>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const { data: fileData, error: downloadError } = await supabase.storage.from('resources').download(resource.file_path);

                        if (downloadError || !fileData) {
                          throw downloadError || new Error('Unable to download resource');
                        }

                        const blobUrl = URL.createObjectURL(fileData);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = resource.file_name || 'resource';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                      } catch (err) {
                        console.error('Failed to download resource', err);
                        alert('Unable to download resource. Please contact your guide.');
                      }
                    }}
                    className="rounded border border-[#304031] bg-transparent px-3 py-1 text-sm font-medium text-[#304031] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#304031] focus-visible:ring-offset-2"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (showGuideProfile && guide) {
    const guideDetails = convertToGuideDetails(guide);
    if (guideDetails) {
      return (
        <GuideProfileView
          guide={guideDetails}
          matchScore={isMatchActive ? data?.match?.match_score : undefined}
          onBack={handleCloseProfile}
          onScheduleConsultation={handleCloseProfile}
          hasScheduledConsultation={hasScheduledConsultation}
        />
      );
    }
  }

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>
        <main className="flex flex-1 min-h-0 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl px-6 py-6">
            {dashboardView}
          </div>
        </main>

        {showRescheduleModal && sessionForModal && (
          <RescheduleModal
            session={sessionForModal}
            onClose={handleCloseReschedule}
            onRescheduleComplete={handleRescheduleComplete}
          />
        )}
      </div>
    </div>
  );
};

export default HomeDashboard;
